import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { DESIGN_PROJECTS, CREATIVE_LEAD, MANAGED_SERVICES } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const managedDesign = MANAGED_SERVICES.find((s) => s.id === 'managed-design')!

const PROBLEMS = [
  {
    problem: 'Every poster looks like a different organization',
    description:
      'One volunteer made the fall poster, the office made the spring one, and neither matches the website. Donors and presenters notice the drift even when they cannot name it.',
  },
  {
    problem: 'Design lands on whoever has an evening free',
    description:
      'Your section leader with Canva is generous, not a designer. The hours they spend fighting templates are hours they are not doing what you actually brought them in to do.',
  },
  {
    problem: 'Season materials arrive in a rush',
    description:
      'Programs get finished the week of the concert and posters go up late. The design was always going to happen; it just never got the time it needed.',
  },
  {
    problem: 'Your logo does not survive contact with print',
    description:
      'It was made for a website header, and now it is blurry on a banner and unreadable on a program spine. A working identity has to hold up everywhere your name appears.',
  },
]

const FAQS = [
  {
    q: 'Do we need to be a Fugue Lab website client?',
    a: 'No. Design projects stand on their own. They do pair naturally with a site we run, because new graphics land straight in your website, email, and social without a handoff.',
  },
  {
    q: 'What does Managed Design cover month to month?',
    a: `For $${managedDesign.priceMonthly} a month you send design requests as they come up: a concert announcement, a program cover, social graphics for the season. One request is active at a time, with a 72-hour turnaround.`,
  },
  {
    q: 'What if we need a whole season designed at once?',
    a: 'That is the season campaign project: announcement graphics, program covers, social templates, and print pieces designed together as one family, priced as a single engagement.',
  },
  {
    q: 'Who owns the work?',
    a: 'You do. Source files and print-ready exports are yours, delivered in the formats your printers and platforms ask for. Same policy as everything else here: you can leave with everything.',
  },
]

export default function GraphicDesignPage() {
  const breadcrumbs = generateBreadcrumbs('/services/design', breadcrumbLabels)

  useSEO({
    title: 'Graphic Design for Music Organizations',
    description:
      'Brand identity, posters, concert programs, and season campaigns for music organizations. One-time projects from $250, or a design retainer at $300/mo.',
    url: '/services/design',
    canonical: '/services/design',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Graphic Design for Music Organizations',
      description:
        'Brand identity, print, and season campaign design for festivals, churches, choruses, and music schools.',
      url: '/services/design',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const projectsRef = useStaggeredGrid<HTMLDivElement>('.project-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Design that carries your season</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your audience meets the poster before the performance. Brand identity, print, and campaign design made for
          music organizations, by a working artist.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          One-time projects, a monthly design retainer, or both. Everything delivered in the formats your printers and
          platforms ask for.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/services/photography">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Photography
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">The gap between how you sound and how you look</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Who does the work */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">An artist, not an agency queue</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">{CREATIVE_LEAD.credit}</p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          A season designed by one hand stays one family from the first poster to the last program, and every request
          goes to the person who will actually draw it.
        </p>
      </section>

      {/* Projects */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">The projects</h2>
        <div ref={projectsRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {DESIGN_PROJECTS.map((project) => (
            <motion.div
              key={project.id}
              className="project-card border-border flex flex-col space-y-3 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{project.name}</h3>
              <div className="text-primary text-lg font-bold">{project.price}</div>
              <p className="text-muted-foreground flex-1 text-sm">{project.note}</p>
            </motion.div>
          ))}
          <motion.div
            className="project-card border-primary/50 bg-primary/5 flex flex-col space-y-3 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary text-sm font-medium">Done For You</div>
            <h3 className="text-foreground text-lg font-semibold">{managedDesign.name}</h3>
            <div className="text-primary text-lg font-bold">${managedDesign.priceMonthly}/mo</div>
            <p className="text-muted-foreground flex-1 text-sm">{managedDesign.note}</p>
            <Link to="/services/done-for-you#managed-design" className="block pt-2">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More
              </Button>
            </Link>
          </motion.div>
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center text-sm">
          Want the whole visual side handled alongside your website and email? That is{' '}
          <Link to="/services/done-for-you#concierge" className="text-primary underline">
            Concierge
          </Link>
          , where this capacity is already included.
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-3 pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
          <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
            <Check className="text-primary h-4 w-4" strokeWidth={2.5} />
            <span>Every project ends with you owning the files, full resolution, no strings.</span>
          </div>
        </div>
      </section>
    </div>
  )
}
